import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Minus, ChevronLeft, Link2, QrCode } from 'lucide-react';
import { playClick, playSuccess } from '@/utils/sounds';
import SlotLinkQR from '@/components/SlotLinkQR';
import QuickLoginModal from '@/components/QuickLoginModal';
import { AvatarCanvasSmall } from '@/components/AvatarCanvas';
import {
  RetroBackground,
  RetroPanel,
  RetroButton,
  RetroPageTitle,
  bandedGradient,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styled input component - moved outside to prevent re-renders
const StyledInput = ({ value, onChange, placeholder, ...props }) => (
  <input
    type="text"
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      fontFamily: DESIGN_TOKENS.fonts.ui,
      fontSize: '14px',
      padding: '12px 14px',
      background: '#FFFFFF',
      color: PALETTE.ink,
      border: `3px solid ${PALETTE.ink}`,
      boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.2)',
      outline: 'none',
      width: '100%',
      borderRadius: DESIGN_TOKENS.borderRadius.md,
    }}
    {...props}
  />
);

export default function NewGameSetup() {
  const navigate = useNavigate();
  const { setupGame, setLinkedAccounts } = useGame();
  const { user, getAuthHeader } = useAuth();
  
  const [step, setStep] = useState(1);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedInnings, setSelectedInnings] = useState(9);
  const [loadingPlaylists, setLoadingPlaylists] = useState(true);
  
  const [teamAName, setTeamAName] = useState('');
  const [teamAPlayers, setTeamAPlayers] = useState(['']);
  
  const [teamBName, setTeamBName] = useState('');
  const [teamBPlayers, setTeamBPlayers] = useState(['']);
  
  const [loading, setLoading] = useState(false);
  
  const [linkedAccounts, setLocalLinkedAccounts] = useState({});
  const [showQRModal, setShowQRModal] = useState(null);
  const [showManualLogin, setShowManualLogin] = useState(null);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    setLoadingPlaylists(true);
    try {
      const response = await axios.get(`${API}/apple-music/playlists`);
      setPlaylists(response.data);
      if (response.data.length > 0) {
        setSelectedPlaylist(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load playlists:', error);
      toast.error('Failed to load playlists');
    } finally {
      setLoadingPlaylists(false);
    }
  };

  const addPlayer = (team) => {
    playClick();
    if (team === 'A' && teamAPlayers.length < 9) {
      setTeamAPlayers([...teamAPlayers, '']);
    } else if (team === 'B' && teamBPlayers.length < 9) {
      setTeamBPlayers([...teamBPlayers, '']);
    }
  };

  const removePlayer = (team, index) => {
    playClick();
    if (team === 'A' && teamAPlayers.length > 1) {
      setTeamAPlayers(teamAPlayers.filter((_, i) => i !== index));
      const key = `${team}-${index}`;
      if (linkedAccounts[key]) {
        const updated = { ...linkedAccounts };
        delete updated[key];
        setLocalLinkedAccounts(updated);
      }
    } else if (team === 'B' && teamBPlayers.length > 1) {
      setTeamBPlayers(teamBPlayers.filter((_, i) => i !== index));
      const key = `${team}-${index}`;
      if (linkedAccounts[key]) {
        const updated = { ...linkedAccounts };
        delete updated[key];
        setLocalLinkedAccounts(updated);
      }
    }
  };

  const updatePlayer = (team, index, value) => {
    if (team === 'A') {
      const updated = [...teamAPlayers];
      updated[index] = value;
      setTeamAPlayers(updated);
    } else {
      const updated = [...teamBPlayers];
      updated[index] = value;
      setTeamBPlayers(updated);
    }
  };

  const handleSelectPlaylist = (playlist) => {
    playClick();
    setSelectedPlaylist(playlist);
  };

  const handleSelectInnings = (innings) => {
    playClick();
    setSelectedInnings(innings);
  };

  const handleShowQR = (team, index) => {
    playClick();
    setShowQRModal({ team, index });
  };

  const handleSlotClaimed = ({ team, slotIndex, user: claimedUser }) => {
    const key = `${team}-${slotIndex}`;
    setLocalLinkedAccounts(prev => ({
      ...prev,
      [key]: {
        userId: claimedUser.user_id,
        username: claimedUser.username,
        avatar_config: claimedUser.avatar_config,
      }
    }));
    
    if (team === 'A') {
      const updated = [...teamAPlayers];
      updated[slotIndex] = claimedUser.username;
      setTeamAPlayers(updated);
    } else {
      const updated = [...teamBPlayers];
      updated[slotIndex] = claimedUser.username;
      setTeamBPlayers(updated);
    }
  };

  const handleManualLoginSuccess = ({ token, user: linkedUser, playerSlot }) => {
    const { team, index } = showManualLogin;
    const key = `${team}-${index}`;
    
    setLocalLinkedAccounts(prev => ({
      ...prev,
      [key]: {
        token,
        userId: linkedUser.id,
        username: linkedUser.username,
        avatar_config: linkedUser.avatar_config || {},
      }
    }));
    
    if (team === 'A') {
      const updated = [...teamAPlayers];
      updated[index] = linkedUser.username;
      setTeamAPlayers(updated);
    } else {
      const updated = [...teamBPlayers];
      updated[index] = linkedUser.username;
      setTeamBPlayers(updated);
    }
    
    setShowManualLogin(null);
  };

  const handleUnlinkAccount = (team, index) => {
    playClick();
    const key = `${team}-${index}`;
    const updated = { ...linkedAccounts };
    delete updated[key];
    setLocalLinkedAccounts(updated);
    
    if (team === 'A') {
      const players = [...teamAPlayers];
      players[index] = '';
      setTeamAPlayers(players);
    } else {
      const players = [...teamBPlayers];
      players[index] = '';
      setTeamBPlayers(players);
    }
    
    toast.success('Account unlinked');
  };

  const handleNext = () => {
    playClick();
    if (step === 1) {
      if (!selectedPlaylist) {
        toast.error('Please select a playlist');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!teamAName.trim()) {
        toast.error('Please enter Team A name');
        return;
      }
      const validPlayers = teamAPlayers.filter(p => p.trim());
      if (validPlayers.length === 0) {
        toast.error('Please add at least one player to Team A');
        return;
      }
      setTeamAPlayers(validPlayers.length > 0 ? validPlayers : ['']);
      setStep(3);
    }
  };

  const handleBack = () => {
    playClick();
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleStartGame = async () => {
    playSuccess();
    if (!teamBName.trim()) {
      toast.error('Please enter Team B name');
      return;
    }
    
    const validPlayersB = teamBPlayers.filter(p => p.trim());
    if (validPlayersB.length === 0) {
      toast.error('Please add at least one player to Team B');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/apple-music/playlists/${selectedPlaylist.id}`);
      const playlist = response.data;
      
      if (!playlist.songs || playlist.songs.length === 0) {
        toast.error('This playlist has no playable songs. Try another playlist.');
        setLoading(false);
        return;
      }
      
      const validPlayersA = teamAPlayers.filter(p => p.trim());
      
      if (typeof setLinkedAccounts === 'function') {
        setLinkedAccounts(linkedAccounts);
      }
      
      setupGame(
        playlist,
        selectedInnings,
        { name: teamAName.trim(), players: validPlayersA },
        { name: teamBName.trim(), players: validPlayersB },
        playlist.songs
      );
      
      navigate('/coin-toss');
    } catch (error) {
      console.error('Failed to start game:', error);
      toast.error('Failed to start game. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLinked = (team, index) => {
    return linkedAccounts[`${team}-${index}`];
  };

  const getCurrentTeamName = () => {
    if (showQRModal?.team === 'A') return teamAName;
    if (showQRModal?.team === 'B') return teamBName;
    return '';
  };

  const renderPlayerRow = (team, player, index, players) => {
    const linked = isLinked(team, index);
    
    return (
      <div key={index} className="flex gap-2 items-center">
        <span style={{
          fontFamily: DESIGN_TOKENS.fonts.ui,
          fontSize: '10px',
          color: PALETTE.ink,
          width: '24px',
        }}>
          {index + 1}.
        </span>
        
        {linked ? (
          <div 
            className="flex-1 flex items-center gap-2 p-2"
            style={{
              border: `2px solid ${PALETTE.grass}`,
              background: `${PALETTE.grass}22`,
            }}
          >
            {linked.avatar_config && (
              <AvatarCanvasSmall config={linked.avatar_config} size={32} />
            )}
            <span style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '9px',
              color: PALETTE.grass,
              flex: 1,
            }}>
              {linked.username}
            </span>
            <button
              onClick={() => handleUnlinkAccount(team, index)}
              style={{
                padding: '4px 8px',
                background: PALETTE.grass,
                border: `2px solid ${PALETTE.ink}`,
                cursor: 'pointer',
              }}
              title="Unlink account"
              data-testid={`unlink-${team.toLowerCase()}-${index}`}
            >
              <Link2 size={12} color={PALETTE.cream} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1">
              <StyledInput
                value={player}
                onChange={(e) => updatePlayer(team, index, e.target.value)}
                placeholder={`Guest ${index + 1}`}
                data-testid={`team-${team.toLowerCase()}-player-${index}`}
              />
            </div>
            
            <button
              onClick={() => handleShowQR(team, index)}
              style={{
                padding: '10px',
                background: PALETTE.grass,
                border: `2px solid ${PALETTE.ink}`,
                cursor: 'pointer',
              }}
              title="Link account via QR code"
              data-testid={`link-qr-${team.toLowerCase()}-${index}`}
            >
              <QrCode size={16} color={PALETTE.cream} />
            </button>
          </>
        )}
        
        {players.length > 1 && !linked && (
          <button
            onClick={() => removePlayer(team, index)}
            style={{
              padding: '10px',
              background: '#C53030',
              border: `2px solid ${PALETTE.ink}`,
              cursor: 'pointer',
            }}
            data-testid={`remove-player-${team.toLowerCase()}-${index}`}
          >
            <Minus size={16} color={PALETTE.cream} />
          </button>
        )}
      </div>
    );
  };

  return (
    <RetroBackground variant={step === 1 ? 'brownStripes' : 'tanStripes'} className="p-4 md:p-8">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <RetroButton
            variant={step === 1 ? 'secondary' : 'tertiary'}
            size="sm"
            onClick={handleBack}
            data-testid="back-btn"
          >
            <ChevronLeft size={20} />
          </RetroButton>
          <div className="flex-1">
            <RetroPageTitle color={step === 1 ? 'cream' : 'ink'} size="md">
              {step === 1 ? 'GAME SETUP' : step === 2 ? 'TEAM A' : 'TEAM B'}
            </RetroPageTitle>
          </div>
          {/* Spacer to keep title centered */}
          <div style={{ width: '52px' }} />
        </div>

        {/* Step 1: Playlist & Innings */}
        {step === 1 && (
          <RetroPanel title="SELECT PLAYLIST" className="mb-6">
            {loadingPlaylists ? (
              <div className="text-center py-8">
                <p style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '10px',
                  color: PALETTE.ink,
                }}>
                  Loading playlists...
                </p>
              </div>
            ) : playlists.length === 0 ? (
              <div className="text-center py-8">
                <p style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '10px',
                  color: `${PALETTE.ink}99`,
                }}>
                  No playlists available yet.
                </p>
              </div>
            ) : (
              <div className="space-y-2 mb-6">
                {playlists.map((playlist) => {
                  const isSelected = selectedPlaylist?.id === playlist.id;
                  return (
                    <button
                      key={playlist.id}
                      onClick={() => handleSelectPlaylist(playlist)}
                      className="w-full px-4 py-2 text-center transition-all"
                      style={{
                        border: `3px solid ${PALETTE.ink}`,
                        borderRadius: DESIGN_TOKENS.borderRadius.pill,
                        background: isSelected
                          ? bandedGradient(ACCENTS.goldLight, ACCENTS.gold, ACCENTS.goldDark)
                          : bandedGradient('#FFFFFF', '#FFFFFF', PALETTE.cream),
                        boxShadow: isSelected
                          ? `inset 1px 1px 0px #FFF3C4, 2px 2px 0px ${PALETTE.ink}`
                          : `2px 2px 0px ${PALETTE.ink}`,
                      }}
                      data-testid={`playlist-${playlist.id}`}
                    >
                      <span style={{
                        fontFamily: DESIGN_TOKENS.fonts.ui,
                        fontWeight: isSelected ? 700 : 600,
                        fontSize: '14px',
                        color: PALETTE.ink,
                        display: 'block',
                      }}>
                        {playlist.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            
            <div className="mb-5">
              <h3 
                className="text-center"
                style={{
                  fontFamily: DESIGN_TOKENS.fonts.display,
                  fontSize: DESIGN_TOKENS.fontSizes.displaySm,
                  color: PALETTE.grass,
                  marginBottom: '12px',
                  letterSpacing: '1px',
                  textShadow: `2px 2px 0px ${PALETTE.ink}22`,
                }}
              >
                SELECT INNINGS
              </h3>
              
              <div className="flex gap-2 justify-center">
                {[3, 5, 7, 9].map((innings) => {
                  const isSelected = selectedInnings === innings;
                  return (
                    <button
                      key={innings}
                      onClick={() => handleSelectInnings(innings)}
                      className="transition-all"
                      style={{
                        width: '56px',
                        height: '52px',
                        border: `3px solid ${PALETTE.ink}`,
                        borderRadius: DESIGN_TOKENS.borderRadius.md,
                        background: isSelected
                          ? bandedGradient(ACCENTS.goldLight, ACCENTS.gold, ACCENTS.goldDark)
                          : bandedGradient('#FFFFFF', '#FFFFFF', PALETTE.cream),
                        color: PALETTE.ink,
                        fontFamily: DESIGN_TOKENS.fonts.display,
                        fontSize: '16px',
                        boxShadow: isSelected
                          ? `inset 1px 1px 0px #FFF3C4, 2px 2px 0px ${PALETTE.ink}`
                          : `2px 2px 0px ${PALETTE.ink}`,
                      }}
                      data-testid={`innings-${innings}`}
                    >
                      {innings}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <RetroButton
              variant="primary"
              size="md"
              fullWidth
              onClick={handleNext}
              data-testid="next-btn-step1"
            >
              NEXT
            </RetroButton>
          </RetroPanel>
        )}

        {/* Step 2: Team A Setup */}
        {step === 2 && (
          <RetroPanel title="TEAM A ROSTER" className="mb-6">
            <div className="mb-4">
              <label style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '9px',
                color: PALETTE.ink,
                display: 'block',
                marginBottom: '8px',
              }}>
                TEAM NAME
              </label>
              <StyledInput
                value={teamAName}
                onChange={(e) => setTeamAName(e.target.value)}
                placeholder="Enter team name"
                data-testid="team-a-name-input"
              />
            </div>
            
            <div className="flex justify-between items-center mb-4">
              <span style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '9px',
                color: PALETTE.ink,
              }}>
                BATTING ORDER
              </span>
              <span style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '7px',
                color: `${PALETTE.ink}99`,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <QrCode size={10} />
                = Link for XP
              </span>
            </div>
            
            <div className="space-y-3 mb-4">
              {teamAPlayers.map((player, index) => 
                renderPlayerRow('A', player, index, teamAPlayers)
              )}
            </div>
            
            {teamAPlayers.length < 9 && (
              <RetroButton
                variant="secondary"
                size="sm"
                fullWidth
                onClick={() => addPlayer('A')}
                className="mb-4"
                data-testid="add-player-a-btn"
              >
                <Plus size={14} className="inline mr-2" />
                ADD PLAYER
              </RetroButton>
            )}
            
            <RetroButton
              variant="primary"
              size="md"
              fullWidth
              onClick={handleNext}
              data-testid="next-btn-step2"
            >
              NEXT
            </RetroButton>
          </RetroPanel>
        )}

        {/* Step 3: Team B Setup */}
        {step === 3 && (
          <RetroPanel title="TEAM B ROSTER" variant="secondary" className="mb-6">
            <div className="mb-4">
              <label style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '9px',
                color: PALETTE.ink,
                display: 'block',
                marginBottom: '8px',
              }}>
                TEAM NAME
              </label>
              <StyledInput
                value={teamBName}
                onChange={(e) => setTeamBName(e.target.value)}
                placeholder="Enter team name"
                data-testid="team-b-name-input"
              />
            </div>
            
            <div className="flex justify-between items-center mb-4">
              <span style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '9px',
                color: PALETTE.ink,
              }}>
                BATTING ORDER
              </span>
              <span style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '7px',
                color: `${PALETTE.ink}99`,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <QrCode size={10} />
                = Link for XP
              </span>
            </div>
            
            <div className="space-y-3 mb-4">
              {teamBPlayers.map((player, index) => 
                renderPlayerRow('B', player, index, teamBPlayers)
              )}
            </div>
            
            {teamBPlayers.length < 9 && (
              <RetroButton
                variant="secondary"
                size="sm"
                fullWidth
                onClick={() => addPlayer('B')}
                className="mb-4"
                data-testid="add-player-b-btn"
              >
                <Plus size={14} className="inline mr-2" />
                ADD PLAYER
              </RetroButton>
            )}
            
            <RetroButton
              variant="primary"
              size="md"
              fullWidth
              onClick={handleStartGame}
              disabled={loading}
              data-testid="start-game-btn"
            >
              {loading ? 'LOADING...' : 'START GAME'}
            </RetroButton>
          </RetroPanel>
        )}

        {/* Progress Indicator */}
        <div className="flex justify-center gap-3 mt-6">
          {[1, 2, 3].map((s) => {
            const dotColor = step === 1 ? PALETTE.cream : PALETTE.dirt;
            return (
              <div
                key={s}
                className="rounded-full"
                style={{
                  width: '12px',
                  height: '12px',
                  border: `2px solid ${dotColor}`,
                  background: s === step ? dotColor : 'transparent',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <SlotLinkQR
          team={showQRModal.team}
          slotIndex={showQRModal.index}
          teamName={getCurrentTeamName()}
          authHeader={getAuthHeader()}
          onClaimed={handleSlotClaimed}
          onClose={() => setShowQRModal(null)}
          onFallbackLogin={() => {
            setShowManualLogin(showQRModal);
            setShowQRModal(null);
          }}
        />
      )}

      {/* Manual Login Fallback Modal */}
      {showManualLogin && (
        <QuickLoginModal
          playerSlot={showManualLogin.index}
          playerName={`Player ${showManualLogin.index + 1}`}
          onSuccess={handleManualLoginSuccess}
          onClose={() => setShowManualLogin(null)}
          onSkip={() => setShowManualLogin(null)}
        />
      )}
    </RetroBackground>
  );
}
